import React from 'react';
import { HomeAPI } from '@/services/residents/api/HomeAPI';
import { Bulletin } from '@/types/residents/bulletin';

type UseResidentBulletinsOptions = {
  propertyId?: number | null;
  onError?: (error: unknown, retry: () => void) => void;
  pageSize?: number; // default 25
  throttleMs?: number; // default 500ms
};

type UseResidentBulletinsResult = {
  items: Bulletin[];
  isLoading: boolean; // initial page
  isFetching: boolean; // subsequent pages
  hasMore: boolean;
  reload: () => Promise<void>;
  loadNext: () => Promise<void>;
};

export const useResidentBulletins = (options: UseResidentBulletinsOptions): UseResidentBulletinsResult => {
  const propertyId = options.propertyId ?? undefined;
  const pageSize = options.pageSize ?? 25;
  const throttleMs = options.throttleMs ?? 500;
  const onErrorRef = React.useRef<UseResidentBulletinsOptions['onError']>(options.onError);
  
  // Update the ref without causing re-renders
  onErrorRef.current = options.onError;

  const [items, setItems] = React.useState<Bulletin[]>([]);
  const [isLoading, setIsLoading] = React.useState(false);
  const [isFetching, setIsFetching] = React.useState(false);
  const [hasMore, setHasMore] = React.useState(true);
  const [pageNum, setPageNum] = React.useState(0);

  const lastRequestedPageRef = React.useRef<number>(-1);
  const lastFetchTsRef = React.useRef<number>(0);
  const isMountedRef = React.useRef<boolean>(true);
  const inFlightRef = React.useRef<boolean>(false);

  const runFetch = React.useCallback(async (page: number) => {
    if (propertyId === undefined || propertyId === null) return;
    const now = Date.now();
    // Prevent overlapping fetches for any page
    if (inFlightRef.current) return;
    if (page > 0) {
      if (lastRequestedPageRef.current === page) return;
      if (now - lastFetchTsRef.current < throttleMs) return;
    }

    lastRequestedPageRef.current = page;
    lastFetchTsRef.current = now;

    if (page === 0) setIsLoading(true);
    else setIsFetching(true);
    inFlightRef.current = true;
    try {
      const data = await HomeAPI.getBulletins(propertyId, page);
      if (!isMountedRef.current) return;
      setItems((prev) => (page === 0 ? data : [...prev, ...data]));
      setHasMore((data?.length ?? 0) === pageSize);
      if ((data?.length ?? 0) > 0) {
        setPageNum(page);
      }
    } catch (e) {
      const cb = onErrorRef.current;
      if (cb) {
        const retry = () => { runFetch(page); };
        cb(e, retry);
      }
    } finally {
      if (!isMountedRef.current) return;
      setIsLoading(false);
      setIsFetching(false);
      inFlightRef.current = false;
    }
  }, [propertyId, pageSize, throttleMs]);

  const reload = React.useCallback(async () => {
    lastRequestedPageRef.current = -1;
    lastFetchTsRef.current = 0;
    setPageNum(0);
    await runFetch(0);
  }, [runFetch]);

  const loadNext = React.useCallback(async () => {
    if (!hasMore) return;
    const next = pageNum + 1;
    await runFetch(next);
  }, [hasMore, pageNum, runFetch]);

  React.useEffect(() => {
    isMountedRef.current = true;
    if (propertyId !== undefined && propertyId !== null) {
      lastRequestedPageRef.current = -1;
      lastFetchTsRef.current = 0;
      setPageNum(0);
      runFetch(0);
    } else {
      setItems([]);
      setHasMore(true);
      setPageNum(0);
    }
    return () => { 
      isMountedRef.current = false; 
    };
  }, [propertyId]);

  return { items, isLoading, isFetching, hasMore, reload, loadNext };
};


